/**
 * Slide-in panel for backfilling missed Google Ads conversion uploads.
 *
 * Historical gap: converted-lead checks that completed BEFORE a location was
 * switched to google_ads delivery never had a Data Manager upload attempted
 * (the cron only uploads at the tag-apply moment). This panel lists affected
 * locations (level 1) and their contacts (level 2), and triggers uploads via
 * the qualifier's /api/internal/backfill-conversion endpoint — one contact at
 * a time or "Upload all" sequentially.
 *
 * Detection is absence-based (see get_ghl_backfill_* RPCs): uploading creates
 * the ledger row, so contacts — and, once empty, locations — drop out of the
 * lists automatically on refetch. Failed attempts also create ledger rows and
 * are triaged from Failed Jobs → Google Ads instead of re-appearing here.
 */

import { useState } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  CloudUpload,
  DatabaseBackup,
  Loader2,
} from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  useBackfillLocations,
  useBackfillContacts,
  useBackfillConversion,
  type BackfillLocation,
  type BackfillContact,
} from '@/hooks/useGHLQualifierDashboard'
import { formatRelativeTime, truncateId } from '@/lib/ghl-qualifier'
import { useToast } from '@/hooks/use-toast'

interface Props {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
}

type RowStatus = 'uploading' | 'uploaded' | 'failed'

interface RunState {
  readonly running: boolean
  readonly done: number
  readonly total: number
  readonly statuses: Readonly<Record<string, RowStatus>>
}

const IDLE_RUN: RunState = { running: false, done: 0, total: 0, statuses: {} }

/** Ledger statuses that count as a successful send (or queued for retry). */
const OK_STATUSES = new Set(['uploaded', 'validated', 'pending', 'processing'])

function LoadingSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-20 w-full" />
      ))}
    </div>
  )
}

export function BackfillPanel({ open, onOpenChange }: Props) {
  const [selected, setSelected] = useState<BackfillLocation | null>(null)
  const [run, setRun] = useState<RunState>(IDLE_RUN)
  // Off by default = only conversions after each location's Google Ads enable
  // date (no double-count risk). Production's known 863 misses ALL predate
  // enablement, so this must be ON to see them — at the cost of possibly
  // re-sending conversions the operator's old GHL workflow already uploaded.
  const [includePreEnablement, setIncludePreEnablement] = useState(false)
  const locations = useBackfillLocations(open, includePreEnablement)
  const contacts = useBackfillContacts(
    selected?.location_id ?? null,
    open,
    includePreEnablement
  )
  const backfill = useBackfillConversion()
  const { toast } = useToast()

  const handleOpenChange = (next: boolean) => {
    if (run.running) return // don't close mid-run
    if (!next) setSelected(null)
    onOpenChange(next)
  }

  const goBack = () => {
    if (run.running) return
    setSelected(null)
    setRun(IDLE_RUN)
  }

  async function uploadOne(contact: BackfillContact) {
    if (!selected) return
    try {
      const result = await backfill.mutateAsync({
        contactId: contact.contact_id,
        locationId: selected.location_id,
      })
      const ok = OK_STATUSES.has(result.status ?? '')
      toast({
        title: ok ? 'Conversion uploaded' : `Upload recorded as ${result.status}`,
        description: ok
          ? `Sent with original conversion time ${new Date(contact.converted_at).toLocaleString()}.`
          : (result.error ??
            'The attempt was recorded — failed rows are retryable from Failed Jobs → Google Ads.'),
        variant: ok ? undefined : 'destructive',
      })
    } catch (err) {
      toast({
        title: 'Backfill failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      })
    }
  }

  async function uploadAll() {
    if (!selected || !contacts.data || contacts.data.length === 0) return
    const snapshot = contacts.data
    setRun({ running: true, done: 0, total: snapshot.length, statuses: {} })

    let uploaded = 0
    let failed = 0
    for (const [i, contact] of snapshot.entries()) {
      setRun((prev) => ({
        ...prev,
        done: i,
        statuses: { ...prev.statuses, [contact.contact_id]: 'uploading' },
      }))
      let rowStatus: RowStatus
      try {
        const result = await backfill.mutateAsync({
          contactId: contact.contact_id,
          locationId: selected.location_id,
        })
        rowStatus = OK_STATUSES.has(result.status ?? '') ? 'uploaded' : 'failed'
      } catch {
        rowStatus = 'failed'
      }
      if (rowStatus === 'uploaded') uploaded++
      else failed++
      setRun((prev) => ({
        ...prev,
        done: i + 1,
        statuses: { ...prev.statuses, [contact.contact_id]: rowStatus },
      }))
    }

    setRun((prev) => ({ ...prev, running: false }))
    toast({
      title: 'Backfill run complete',
      description: `${uploaded} uploaded, ${failed} failed of ${snapshot.length}. ${
        failed > 0 ? 'Failed attempts are visible in Failed Jobs → Google Ads.' : ''
      }`,
      variant: failed > 0 ? 'destructive' : undefined,
    })
  }

  function renderLocationRow(loc: BackfillLocation) {
    return (
      <button
        key={loc.location_id}
        type="button"
        onClick={() => setSelected(loc)}
        className="w-full text-left rounded-md border border-border bg-card hover:bg-muted/40 transition-colors p-3 group"
      >
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm truncate">{loc.location_name}</span>
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px] py-0 px-1.5">
                {loc.missing_count} missing
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Google Ads enabled {new Date(loc.google_ads_enabled_at).toLocaleDateString()}
            </p>
            <p className="text-[10px] text-muted-foreground/50 font-mono">
              loc: {loc.location_id}
            </p>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors flex-shrink-0" />
        </div>
      </button>
    )
  }

  function renderContactRow(contact: BackfillContact) {
    const displayName = contact.contact_name?.trim() || truncateId(contact.contact_id)
    const rowStatus = run.statuses[contact.contact_id]
    const singleUploading =
      !run.running &&
      backfill.isPending &&
      backfill.variables?.contactId === contact.contact_id

    return (
      <div
        key={contact.contact_id}
        className="rounded-md border border-border bg-card p-3 space-y-1.5"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm truncate">{displayName}</span>
          {contact.conversion_value != null && (
            <Badge variant="outline" className="text-[10px] py-0 px-1.5 font-normal">
              ${contact.conversion_value}
            </Badge>
          )}
          {rowStatus === 'uploaded' && (
            <Badge className="bg-green-100 text-green-800 hover:bg-green-100 text-[10px] py-0 px-1.5">
              uploaded
            </Badge>
          )}
          {rowStatus === 'failed' && (
            <Badge className="bg-red-100 text-red-800 hover:bg-red-100 text-[10px] py-0 px-1.5">
              failed
            </Badge>
          )}
          <span className="text-muted-foreground/60 ml-auto text-xs">
            converted {formatRelativeTime(contact.converted_at)}
          </span>
        </div>
        {(contact.contact_email || contact.contact_id) && (
          <p className="text-xs text-muted-foreground truncate">
            {contact.contact_email ?? (
              <span className="font-mono">{truncateId(contact.contact_id, 14)}</span>
            )}
          </p>
        )}
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] text-muted-foreground/50 font-mono truncate min-w-0">
            {contact.contact_id}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 h-7 shrink-0"
            disabled={run.running || backfill.isPending || rowStatus === 'uploaded'}
            onClick={() => uploadOne(contact)}
          >
            {singleUploading || rowStatus === 'uploading' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <CloudUpload className="h-3.5 w-3.5" />
            )}
            Upload
          </Button>
        </div>
      </div>
    )
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-3 border-b">
          <div className="flex items-center gap-3">
            {selected && (
              <Button
                variant="ghost"
                size="sm"
                onClick={goBack}
                disabled={run.running}
                className="gap-1 -ml-2"
              >
                <ChevronLeft className="h-4 w-4" />
                Back
              </Button>
            )}
            <div className="space-y-1 flex-1 min-w-0">
              <SheetTitle className="flex items-center gap-2">
                <DatabaseBackup className="h-5 w-5 text-amber-600" />
                {selected ? selected.location_name : 'Google Ads Backfill'}
              </SheetTitle>
              <SheetDescription>
                {selected
                  ? 'Converted contacts with no Google Ads upload. Uploads use the original conversion time.'
                  : 'Accounts with converted contacts that were never uploaded to Google Ads (since each account’s enable date).'}
              </SheetDescription>
            </div>
            {selected && (
              <Button
                size="sm"
                className="gap-1.5 shrink-0"
                disabled={
                  run.running || backfill.isPending || (contacts.data?.length ?? 0) === 0
                }
                onClick={uploadAll}
              >
                {run.running ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <CloudUpload className="h-3.5 w-3.5" />
                )}
                {run.running
                  ? `Uploading ${run.done}/${run.total}…`
                  : `Upload all (${contacts.data?.length ?? 0})`}
              </Button>
            )}
          </div>
        </SheetHeader>

        {!selected && (
          <div className="px-6 py-3 border-b bg-muted/30 flex items-start gap-3">
            <Switch
              id="include-pre-enablement"
              checked={includePreEnablement}
              onCheckedChange={setIncludePreEnablement}
              disabled={run.running}
              className="mt-0.5"
            />
            <div className="space-y-0.5">
              <Label htmlFor="include-pre-enablement" className="text-sm cursor-pointer">
                Include conversions from before Google Ads was enabled
              </Label>
              <p className="text-xs text-muted-foreground">
                Off: only conversions after each account’s enable date (safe — no
                double-counting). Almost all historical misses predate enablement, so
                turn this on to surface them — but the operator’s old GHL workflow may
                have already uploaded some.
              </p>
            </div>
          </div>
        )}

        <ScrollArea className="flex-1 px-6 py-4">
          {selected ? (
            contacts.isLoading ? (
              <LoadingSkeleton />
            ) : (contacts.data?.length ?? 0) === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <p className="text-sm">All conversions for this account are uploaded. 🎉</p>
              </div>
            ) : (
              <div className="space-y-2">{contacts.data!.map(renderContactRow)}</div>
            )
          ) : locations.isLoading ? (
            <LoadingSkeleton />
          ) : (locations.data?.length ?? 0) === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <p className="text-sm">No accounts need backfill. 🎉</p>
            </div>
          ) : (
            <div className="space-y-2">{locations.data!.map(renderLocationRow)}</div>
          )}
        </ScrollArea>

        <div className="border-t px-6 py-3 text-xs text-muted-foreground">
          {selected
            ? 'Uploaded contacts disappear from this list; failures land in Failed Jobs → Google Ads.'
            : `${locations.data?.reduce((sum, l) => sum + Number(l.missing_count), 0) ?? 0} missed conversions across ${locations.data?.length ?? 0} accounts`}
        </div>
      </SheetContent>
    </Sheet>
  )
}
